"use client";

import { useEffect, useSyncExternalStore } from "react";
import { usePathname } from "@rai/ui/adapters/navigation";
import { classifyLink, initializeAnalytics, trackEvent, trackPageView } from "@rai/core/lib/analytics";

const subscribe = () => () => {};
const serverSnapshot = () => false;
/** `localStorage.disable_analytics = "true"` keeps GA off in this browser (internal traffic, QA). */
const analyticsDisabled = () => {
  try {
    return window.localStorage.getItem("disable_analytics") === "true";
  } catch {
    return false;
  }
};

/** One owner for initialization, virtual pageviews, and delegated public interactions. */
export function GoogleAnalytics({ measurementId, adsConversion }: { measurementId: string; adsConversion?: string }) {
  const pathname = usePathname();
  const debug = process.env.NEXT_PUBLIC_GA_DEBUG === "true";
  const enabled = useSyncExternalStore(subscribe, () => {
    const production = process.env.NODE_ENV === "production" && ["raicomputer.com", "www.raicomputer.com", "app.raicomputer.com"].includes(window.location.hostname);
    const optedOut = navigator.doNotTrack === "1" || (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl;
    return (production || debug) && !optedOut && !analyticsDisabled() && /^G-[A-Z0-9]+$/.test(measurementId);
  }, serverSnapshot);

  useEffect(() => {
    if (!enabled) return;
    initializeAnalytics(measurementId, debug, adsConversion);
    const newPage = window.raiAnalytics?.lastPath !== pathname;
    trackPageView(pathname, document.referrer);
    const product = document.querySelector("[data-analytics-item-view]");
    if (newPage && product) trackEvent("view_item", {
      item_id: product.getAttribute("data-analytics-item-view") || undefined,
      item_category: product.getAttribute("data-analytics-category") || undefined,
      tier: product.getAttribute("data-analytics-tier") || undefined,
    });
    const depths = new Set<number>();
    const sections = new Set<string>();
    const forms = new Set<string>();
    const invalidFields = new Set<string>();
    const click = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      const link = target?.closest("a[href]");
      if (!link) return;
      const result = classifyLink(link.getAttribute("href") || "");
      const cardItemId = link.closest("[data-analytics-item]")?.getAttribute("data-analytics-item");
      const itemId = cardItemId || link.closest("[data-analytics-item-view]")?.getAttribute("data-analytics-item-view");
      if (cardItemId && result?.event === "navigation_click") trackEvent("select_item", { item_id: cardItemId });
      if (result) trackEvent(result.event, {
        ...result.params,
        placement: link.closest("header") ? "header" : link.closest("footer") ? "footer" : "content",
        item_id: itemId || undefined,
      });
    };
    const interaction = (event: Event) => {
      const target = event.target instanceof Element ? event.target : null;
      const form = target?.closest("form[data-analytics-form]");
      const id = form?.getAttribute("data-analytics-form");
      if (!id) return;
      if (!forms.has(id)) {
        forms.add(id);
        trackEvent("form_start", { form_id: id });
      }
      if (event.type === "invalid") {
        const field = target?.getAttribute("name") || "unknown";
        const key = `${id}:${field}`;
        if (!invalidFields.has(key)) {
          invalidFields.add(key);
          trackEvent("form_error", { form_id: id, field_name: field, error_type: "validation" });
        }
      }
    };
    let frame = 0;
    const scroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const height = document.documentElement.scrollHeight - window.innerHeight;
        if (height <= 0) return;
        const percent = window.scrollY / height * 100;
        for (const depth of [25, 50, 75, 90]) {
          if (percent >= depth && !depths.has(depth)) {
            depths.add(depth);
            trackEvent("scroll_depth", { percent_scrolled: depth });
          }
        }
      });
    };
    const observer = typeof IntersectionObserver === "undefined" ? null : new IntersectionObserver(entries => {
      for (const entry of entries) {
        const id = entry.target.getAttribute("data-analytics-section") || entry.target.id;
        if (entry.isIntersecting && id && !sections.has(id)) {
          sections.add(id);
          trackEvent("section_view", { section_id: id });
        }
      }
    }, { threshold: 0.1 });
    document.querySelectorAll("main section[id], main [data-analytics-section]").forEach(node => observer?.observe(node));
    document.addEventListener("click", click, true);
    document.addEventListener("input", interaction, true);
    document.addEventListener("change", interaction, true);
    document.addEventListener("invalid", interaction, true);
    document.addEventListener("click", interaction, true);
    window.addEventListener("scroll", scroll, { passive: true });
    return () => {
      document.removeEventListener("click", click, true);
      document.removeEventListener("input", interaction, true);
      document.removeEventListener("change", interaction, true);
      document.removeEventListener("invalid", interaction, true);
      document.removeEventListener("click", interaction, true);
      window.removeEventListener("scroll", scroll);
      cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [enabled, pathname, measurementId, debug, adsConversion]);

  // Loaded once after hydration, like next/script's afterInteractive, so both apps share this loader.
  useEffect(() => {
    if (!enabled || document.getElementById("ga-loader")) return;
    const script = document.createElement("script");
    script.id = "ga-loader";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);
  }, [enabled, measurementId]);

  return null;
}
